// Marks the CURRENT BROWSER as belonging to an internal person (the
// developer or the farmer who runs /admin), not a real customer. Unlike
// consent.ts (which governs whether THIS visitor's own data may be
// collected), this flag governs whether the visitor should be counted at
// all: /admin is already excluded because analytics.ts is never imported
// under src/app/admin/, but both internal people also browse the
// customer-facing pages to check how things look, and the public site
// gets only a handful of real visits a day, so one internal person's
// browsing visibly distorts every number.
//
// Storage-only, no PostHog dependency: analytics.ts checks
// isInternalVisitor() before posthog.init() ever runs, so an internal
// visit costs no quota and needs no person record, and behaves identically
// whether the visitor would otherwise be tier 1 (anonymous/cookieless) or
// tier 2 (consented) — see the file-level comment in analytics.ts.
//
// Ordering guarantee (why the very first ?internal=1 pageview is never
// captured): analytics.ts imports this module before its own module-scope
// `if (typeof window !== "undefined") { initAnalytics(); }` call runs. ES
// modules evaluate an imported module's top-level code to completion
// before the importing module continues past the import statement, so
// syncInternalVisitorFlagFromUrl() below always finishes — including the
// localStorage write — before initAnalytics() gets a chance to call
// isInternalVisitor(). There is no async gap between the two, so no race
// is possible here.

const STORAGE_KEY = "hf_internal_visitor";

export function isInternalVisitor(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // Private mode, storage quota, or storage disabled outright. Fail
    // OPEN: treat the visitor as NOT internal, so a real customer's
    // analytics never silently goes missing just because storage happened
    // to throw. One uncaught internal visit is a far smaller cost than a
    // lost customer pageview.
    return false;
  }
}

function syncInternalVisitorFlagFromUrl(): void {
  const urlSearchParameters = new URLSearchParams(window.location.search);
  const internalFlagValue = urlSearchParameters.get("internal");

  if (internalFlagValue === "1") {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // see isInternalVisitor: on failure the flag simply never gets
      // persisted, so isInternalVisitor() reads back false and this
      // browser falls back to normal analytics instead of getting stuck
      // half-activated.
    }
    return;
  }

  if (internalFlagValue === "0") {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // see isInternalVisitor
    }
  }
}

// Runs once per full page load (module evaluation happens once; a
// client-side route transition within the app does not re-run this).
// Turning the flag off again therefore also needs a full navigation — see
// the plain <a href="?internal=0"> (not next/link) in
// InternalVisitorBadge.tsx.
if (typeof window !== "undefined") {
  syncInternalVisitorFlagFromUrl();
}
